import { appIntl } from '@components/locale/IntlGlobalProvider';
import { ToastStatusEnum } from '@components/ui/toaster';
import { automatonSectionColor, automatonSectionColorHexa, ColorLabelEnum, iconColor } from '@utils/map/map.constants';
import { SHOW_AUTOMATON_SECTION_MAX_ZOOM } from '@utils/map/mapZoom.constants';
import { getTechnicalStateName } from '@utils/map/sensor.utils';
import { createSensorToast, getSensorUniqueKey } from '@utils/sensors/sensors.utils';
import { toLonLatArray } from '@utils/validation/coordinates.utils';
import { Feature, Position } from 'geojson';
import { capitalize, isEqual, uniqWith } from 'lodash';

import { AutomatonSectionDefenceCommand, AutomatonSegmentDefenceCommand } from '@/types/c2/c2.types';
import { Coordinates } from '@/types/commons/commons.types';
import { FeatureTypeEnum } from '@/types/map.types';
import {
  AutomatonCabinet,
  AutomatonSection,
  AutomatonSectionData,
  AutomatonSectionStatus,
  AutomatonSectionStatusEnumWithMaintenance,
  AutomatonSectionUniqueKeys,
  AutomatonSegment,
  AutomatonSegmentTypeEnum,
  AutomatonSensorConfiguration,
  ConnectorStatusEnum,
  EffectiveSensorStatusEnum,
  HealthStatusEnum,
  SegmentSpecificStateEnum,
  SpecificStateEnum,
  SubSensorTypeEnum,
} from '@/types/sensor/sensors';

export function getSectionName(section: AutomatonSection): string {
  return (section.upperSegment?.name ?? section.lowerSegment?.name) as string;
}

export function getSectionNameAndSegments(section: AutomatonSection): { name: string; segments: AutomatonSegment[] } {
  return { name: getSectionName(section), segments: Object.values(section).filter(Boolean) as AutomatonSegment[] };
}

export function getAutomatonSectionKeys(sectionData: AutomatonSectionData): AutomatonSectionUniqueKeys {
  return {
    automatonUniqueKey: getSensorUniqueKey(sectionData.automaton),
    cabinetUniqueKey: getSensorUniqueKey(sectionData.cabinet),
    sectionName: getSectionName(sectionData.section),
  };
}

export function getAutomatonSectionFeatureId(automatonSectionUniqueKeys: AutomatonSectionUniqueKeys): string {
  return `${automatonSectionUniqueKeys.automatonUniqueKey}-${automatonSectionUniqueKeys.cabinetUniqueKey}-${automatonSectionUniqueKeys.sectionName}`;
}

export function getSectionStartAndEndPosition(section: AutomatonSection): {
  sectionStart: Position;
  sectionEnd: Position;
} {
  const segment = section.upperSegment ?? section.lowerSegment;
  const startPoint = segment.segmentPosition[0];
  const endPoint = segment.segmentPosition.at(-1);
  return { sectionStart: toLonLatArray(startPoint, false)!, sectionEnd: toLonLatArray(endPoint, false)! };
}

export function getSectionPositions(section: AutomatonSection) {
  const segment = section.upperSegment ?? section.lowerSegment;
  return segment.segmentPosition.map((coord) => toLonLatArray(coord, false)!);
}

export const toLinePointsFeature = (
  automatonsSectionNames: AutomatonSectionUniqueKeys,
  positions: Position[],
  color?: string,
): Feature => ({
  type: 'Feature',
  id: `${FeatureTypeEnum.AUTOMATON_SECTION_POINT}-${automatonsSectionNames.sectionName}`,
  properties: {
    id: automatonsSectionNames.sectionName,
    featureType: FeatureTypeEnum.AUTOMATON_SECTION_POINT,
    value: automatonsSectionNames,
    color,
    selectOptionLabel: automatonsSectionNames.sectionName,
  },
  geometry: {
    type: 'GeometryCollection',
    geometries: positions.map((position) => ({
      type: 'Point',
      coordinates: position,
    })),
  },
});

export function getSectionCenterCoordinates(section: AutomatonSection, zoom?: number): Coordinates {
  const segment = section.upperSegment ?? section.lowerSegment;
  return zoom && zoom < SHOW_AUTOMATON_SECTION_MAX_ZOOM ? segment.simplifiedPosition : segment.position;
}

export function isSectionActive(
  automaton: AutomatonSensorConfiguration,
  cabinet: AutomatonCabinet,
  section: AutomatonSection,
): boolean {
  if (!automaton.active || !cabinet.active) {
    return false;
  }

  if (!section.upperSegment) {
    return section.lowerSegment.active;
  }
  if (!section.lowerSegment) {
    return section.upperSegment.active;
  }
  return section.lowerSegment.active && section.upperSegment.active;
}

export function isSegmentActive(
  automaton: AutomatonSensorConfiguration,
  cabinet: AutomatonCabinet,
  segment: AutomatonSegment | null,
): boolean {
  return automaton.active && cabinet.active && !!segment?.active;
}

export function isSectionInMaintenance(
  automaton: AutomatonSensorConfiguration,
  cabinet: AutomatonCabinet,
  section: AutomatonSection,
): boolean {
  return (
    automaton.maintenance ||
    cabinet.maintenance ||
    !!section.lowerSegment?.maintenance ||
    !!section.upperSegment?.maintenance
  );
}

export function getSectionIconStatus(
  sectionData: AutomatonSectionData,
  sectionStatus: EffectiveSensorStatusEnum | null,
): AutomatonSectionStatusEnumWithMaintenance {
  const { automaton, cabinet, section } = sectionData;
  if (!isSectionActive(automaton, cabinet, section)) {
    return ConnectorStatusEnum.UNREACHABLE;
  } else if (isSectionInMaintenance(automaton, cabinet, section)) {
    return 'maintenance';
  } else if (!sectionStatus) {
    return ConnectorStatusEnum.UNREACHABLE;
  } else if (sectionStatus === ConnectorStatusEnum.UNREACHABLE) {
    return HealthStatusEnum.DEGRADED;
  }
  return sectionStatus as AutomatonSectionStatusEnumWithMaintenance;
}

export function getSegmentStatus(
  segment: AutomatonSegment,
  sectionStatus: AutomatonSectionStatusEnumWithMaintenance | null,
  automaton: AutomatonSensorConfiguration,
  cabinet: AutomatonCabinet,
): AutomatonSectionStatusEnumWithMaintenance {
  if (!automaton.active || !cabinet.active || !segment.active) {
    return ConnectorStatusEnum.UNREACHABLE;
  } else if (segment.maintenance || cabinet.maintenance || automaton.maintenance) {
    return 'maintenance';
  } else if (!sectionStatus) {
    return ConnectorStatusEnum.UNREACHABLE;
  } else if (sectionStatus === ConnectorStatusEnum.UNREACHABLE) {
    return HealthStatusEnum.DEGRADED;
  }
  return sectionStatus;
}

export function getSectionLineColor(
  sectionData: AutomatonSectionData,
  sectionStatus: AutomatonSectionStatus | undefined,
): string | undefined {
  const { automaton, cabinet, section } = sectionData;
  if (!isSectionActive(automaton, cabinet, section)) {
    return automatonSectionColorHexa.UNREACHABLE;
  } else if (isSectionInMaintenance(automaton, cabinet, section)) {
    return automatonSectionColorHexa.DEGRADED;
  } else if (!sectionStatus) {
    return automatonSectionColorHexa.UNREACHABLE;
  } else if (sectionStatus.effectiveStatus === ConnectorStatusEnum.UNREACHABLE) {
    return automatonSectionColorHexa.DEGRADED;
  }
  return automatonSectionColorHexa[
    sectionStatus.specificState === SpecificStateEnum.EFFRACTION ||
    sectionStatus.specificState === SpecificStateEnum.SLEEP
      ? sectionStatus.specificState
      : sectionStatus.effectiveStatus
  ];
}

export function getSectionPerimeterCameraUniqueKeys(section: AutomatonSection) {
  return uniqWith(
    [section.lowerSegment, section.upperSegment]
      .filter(Boolean)
      .flatMap((segment) => segment!.perimeterCameraUniqueKeys),
    isEqual,
  );
}

export function getAutomatonSectionSpriteIconName(
  iconStatus: AutomatonSectionStatusEnumWithMaintenance,
  specificState: SegmentSpecificStateEnum | null,
): string {
  if (iconStatus === 'maintenance') {
    return `icSymb-tro-work-${iconColor[ColorLabelEnum.ORANGE]}`;
  } else {
    const technicalStateName = getTechnicalStateName(specificState, SubSensorTypeEnum.AUTOMATON_SEGMENT, undefined);
    const showState = iconStatus === HealthStatusEnum.HEALTHY && specificState && technicalStateName !== '';
    if (showState) {
      return `icSymb-tro${technicalStateName}-${automatonSectionColor[specificState]}`;
    }
    return `icSymb-tro-${automatonSectionColor[iconStatus]}`;
  }
}

/**
 *
 * @param command The command to send, for a section or a segment
 * @param enableRepulsion False to disable the repulsion, true to enable it
 * @param sectionName Name of the section to display in the toast
 * @param isSegment true if the command is for a segment. False if it is for a section
 * @param postRepulsionCommand the repulsionCommand Mutation to use
 * @param segmentType If the command is for a segment, must include the type, LOWER or UPPER
 * @param automatonSite the site of the automaton which has the segment or section
 * @param automatonAppCode the app code of the automaton which has the segment or section
 */
export function sendRepulsionCommand(
  command: AutomatonSectionDefenceCommand | AutomatonSegmentDefenceCommand,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  postRepulsionCommand: any,
  enableRepulsion: boolean,
  sectionName: string,
  isSegment: boolean,
  automatonSite: string,
  automatonAppCode: string,
  segmentType: AutomatonSegmentTypeEnum = AutomatonSegmentTypeEnum.UPPER,
) {
  const toastParams = {
    command: `sensors.commands.${isSegment ? 'segment' : 'section'}.defenceCommand`,
    commandValues: {
      state: enableRepulsion,
      name: appIntl().formatMessage({ id: `automatonSection.segment.${segmentType}` }),
    },
    name: sectionName,
  };
  createSensorToast({
    ...toastParams,
    toast: ToastStatusEnum.INFO,
  });
  postRepulsionCommand({
    site: automatonSite,
    appCode: automatonAppCode,
    command,
  })
    .unwrap()
    .then(() =>
      createSensorToast({
        ...toastParams,
        toast: ToastStatusEnum.SUCCESS,
      }),
    )
    .catch(() =>
      createSensorToast({
        ...toastParams,
        toast: ToastStatusEnum.ERROR,
      }),
    );
}

function getTronNumber(value: string) {
  if (value.includes('_Tron')) {
    return value.split('_Tron')[1].split('_')[0];
  }
  return 'UNSPECIFIED';
}

function getPositionSegment(value: string) {
  if (value.includes('_')) {
    return capitalize(value.split('_').pop());
  }
  return 'UNSPECIFIED';
}

export function getAutomatonNodeName(value?: string) {
  const formatMessage = appIntl().formatMessage;
  if (!value) {
    return formatMessage({ id: `global.noInformation` });
  }
  const category = value.split('_Tron')[0];
  switch (category) {
    case 'Secteur.Defaut':
    case 'Secteur.Effraction':
    case 'Secteur.HT_Detection':
      return formatMessage(
        { id: `automatonCabinet.value.${category}` },
        { num: getTronNumber(value), position: getPositionSegment(value) },
      );
    default:
      return formatMessage({ id: `automatonCabinet.value.${value}` });
  }
}
